import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import CoinBalance from '../components/topBar/CoinBalance';
import { appNavigation, getAuthHeaders, getEffectiveRole, SUPPORTED_LANGUAGES, urlMap } from '../../utils';
import { getWebLanguage, tWeb } from '../../i18n/sharedTranslations';
import '../../assets/styles/components/Projects.css';

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(payload?.error || `HTTP_${res.status}`), { body: payload });
  return payload;
}

const AI_SUGGEST_FIG_COST = 1;
const MAX_SIMULATION_AI_ANSWER_CHARS = 200;
const DRAFT_GENERATE_FIG_COST = 5;
const SYSTEM_PROMPT_EXCERPT_MAX_CHARS = 100;
const TASK_COMPLEXITY_OPTIONS = [
  { value: 'easy', labelKey: 'scaffoldEditor.taskComplexity.easy' },
  { value: 'medium', labelKey: 'scaffoldEditor.taskComplexity.medium' },
  { value: 'difficult', labelKey: 'scaffoldEditor.taskComplexity.difficult' },
];

const SCAFFOLD_PUBLISH_VALIDATION_MESSAGES = {
  MISSING_TITLE: 'Add a scaffold title before publishing.',
  MISSING_DESCRIPTION: 'Add a scaffold description before publishing.',
  MISSING_PUBLISHER_NAME: 'Add a publisher name before publishing.',
  INVALID_PRICE_FIG_COINS: 'Set a valid scaffold price before publishing.',
  MISSING_SYSTEM_PROMPT: 'Add a system prompt before publishing.',
  MISSING_TASK_SYSTEM_PROMPT: 'Add a task system prompt before publishing.',
  MISSING_QUESTIONS: 'Add at least one question before publishing.',
  EMPTY_QUESTION_TEXT: 'Fill all questions before publishing.',
};

const languageLabel = (code) => {
  const language = SUPPORTED_LANGUAGES.find((item) => item.code === code);
  return language?.label || (code ? String(code).toUpperCase() : 'English');
};

const isScaffoldEditLocked = (status) => status === 'in_review' || status === 'published' || status === 'active';

const scaffoldStatusLabel = (status) => {
  if (status === 'active' || status === 'published') return 'Published';
  if (status === 'in_review') return 'In Review';
  return 'Draft';
};

const buildExcerpt = (value, maxChars = SYSTEM_PROMPT_EXCERPT_MAX_CHARS) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'No prompt text yet.';
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars).trimEnd()}...`;
};

const normalizeScaffold = (payload) => ({
  id: payload.id,
  title: payload.title || 'Untitled scaffold',
  description: payload.description || '',
  publisherName: payload.publisherName || '',
  status: payload.status || 'draft',
  questionLanguage: payload.questionLanguage || payload.language || 'en',
  systemPrompt: payload.systemPrompt || '',
  taskSystemPrompt: payload.taskSystemPrompt || '',
  metadata: payload.metadata || {},
  questions: Array.isArray(payload.questions)
    ? payload.questions.map((question, index) => ({
        id: question.id || null,
        order: question.order || index + 1,
        text: question.text || '',
        purpose: question.purpose || '',
        answerHint: question.answerHint || '',
        metadata: question.metadata || {},
      }))
    : [],
});

export default function ScaffoldEditorPage() {
  const { scaffoldId } = useParams();
  const navigate = useNavigate();
  const t = (key, vars) => tWeb(getWebLanguage(), key, vars);
  const effectiveRole = getEffectiveRole();
  const canManageScaffolds = effectiveRole === 'publisher' || effectiveRole === 'admin';
  const savedFlashTimerRef = useRef(null);
  const saveStatusTimerRef = useRef(null);
  const simulateChatRef = useRef(null);
  const dirtyFieldNamesRef = useRef(new Set());
  const dirtyQuestionIndexesRef = useRef(new Set());
  const [scaffolds, setScaffolds] = useState([]);
  const [scaffoldsLoading, setScaffoldsLoading] = useState(true);
  const [scaffoldsError, setScaffoldsError] = useState(null);
  const [scaffold, setScaffold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [savedFieldNames, setSavedFieldNames] = useState(new Set());
  const [savedQuestionIndexes, setSavedQuestionIndexes] = useState(new Set());
  const [publishing, setPublishing] = useState(false);
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [simulatePhase, setSimulatePhase] = useState('idle');
  const [simulateMessages, setSimulateMessages] = useState([]);
  const [simulateCurrentAnswer, setSimulateCurrentAnswer] = useState('');
  const [simulateSessionId, setSimulateSessionId] = useState(null);
  const [simulateAiSessionId, setSimulateAiSessionId] = useState(null);
  const [simulateQuestionIndex, setSimulateQuestionIndex] = useState(0);
  const [simulateTotal, setSimulateTotal] = useState(10);
  const [simulateSubmitting, setSimulateSubmitting] = useState(false);
  const [simulateGeneratingDraft, setSimulateGeneratingDraft] = useState(false);
  const [simulateUseAiAnswers, setSimulateUseAiAnswers] = useState(false);
  const [simulateSuggesting, setSimulateSuggesting] = useState(false);
  const [simulateWarnedBlank, setSimulateWarnedBlank] = useState(false);
  const [simulateError, setSimulateError] = useState(null);
  const [simulateHistory, setSimulateHistory] = useState([]);
  const [simulateHistoryLoading, setSimulateHistoryLoading] = useState(false);
  const [simulateHistoryError, setSimulateHistoryError] = useState(null);
  const [simulateHistoryExpanded, setSimulateHistoryExpanded] = useState(true);
  const [simulateHistoryActiveId, setSimulateHistoryActiveId] = useState(null);
  const [systemPromptExpanded, setSystemPromptExpanded] = useState(false);
  const [taskSystemPromptExpanded, setTaskSystemPromptExpanded] = useState(false);
  const [systemScaffoldInfoOpen, setSystemScaffoldInfoOpen] = useState(false);
  const [taskComplexityInfoOpen, setTaskComplexityInfoOpen] = useState(false);
  const [dragQuestionIndex, setDragQuestionIndex] = useState(null);
  const [dragOverQuestionIndex, setDragOverQuestionIndex] = useState(null);
  const [error, setError] = useState(null);

  if (!canManageScaffolds) {
    return <Navigate to={appNavigation.shortStoryChat} replace />;
  }

  const loadScaffolds = useCallback(async () => {
    try {
      setScaffoldsLoading(true);
      setScaffoldsError(null);
      const res = await fetch(urlMap.listAuthorStoryScaffolds(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const payload = await res.json().catch(() => []);
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);
      setScaffolds(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setScaffoldsError(err.message || 'Could not load scaffolds');
    } finally {
      setScaffoldsLoading(false);
    }
  }, []);

  const loadScaffold = useCallback(async () => {
    if (!scaffoldId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(urlMap.getAuthorStoryScaffold(scaffoldId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      let payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);

      const initial = normalizeScaffold(payload);
      const scaffoldLanguage = initial.questionLanguage || 'en';

      // Re-read in scaffold-selected language so editor text follows the scaffold language,
      // not the user profile fallback language.
      if (payload?.language !== scaffoldLanguage) {
        const localizedRes = await fetch(urlMap.getAuthorStoryScaffold(scaffoldId, scaffoldLanguage), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
        });
        const localizedPayload = await localizedRes.json().catch(() => ({}));
        if (!localizedRes.ok) throw new Error(localizedPayload?.error || `HTTP_${localizedRes.status}`);
        payload = localizedPayload;
      }

      setScaffold(normalizeScaffold(payload));
      setSaveState('saved');
    } catch (err) {
      setError(err.message || 'Could not load scaffold');
    } finally {
      setLoading(false);
    }
  }, [scaffoldId]);

  useEffect(() => {
    loadScaffolds();
  }, [loadScaffolds]);

  useEffect(() => {
    loadScaffold();
  }, [loadScaffold]);

  const saveScaffold = useCallback(async (draft) => {
    if (!draft?.id) return;
    try {
      setSaving(true);
      setSaveState('saving');
      setError(null);
      const res = await fetch(urlMap.updateStoryScaffold(draft.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          title: draft.title,
          description: draft.description,
          publisherName: draft.publisherName,
          metadata: {
            ...(draft.metadata || {}),
            questionLanguage: draft.questionLanguage || 'en',
          },
          questionLanguage: draft.questionLanguage,
          systemPrompt: draft.systemPrompt,
          taskSystemPrompt: draft.taskSystemPrompt,
          questions: draft.questions.map((question, index) => ({
            order: question.order || index + 1,
            text: question.text,
            purpose: question.purpose || null,
            answerHint: question.answerHint || null,
            metadata: question.metadata || {},
          })),
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);
      const savedFieldNames = Array.from(dirtyFieldNamesRef.current);
      const savedQuestionIndexes = Array.from(dirtyQuestionIndexesRef.current);
      dirtyFieldNamesRef.current = new Set();
      dirtyQuestionIndexesRef.current = new Set();
      setScaffold({
        ...normalizeScaffold(payload),
        title: draft.title,
        description: draft.description,
        publisherName: draft.publisherName,
      });
      setSaveState('saved');
      if (savedFieldNames.length > 0 || savedQuestionIndexes.length > 0) {
        setSavedFieldNames(new Set(savedFieldNames));
        setSavedQuestionIndexes(new Set(savedQuestionIndexes));
        if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current);
        savedFlashTimerRef.current = setTimeout(() => {
          setSavedFieldNames(new Set());
          setSavedQuestionIndexes(new Set());
        }, 1200);
      }
      await loadScaffolds();
    } catch (err) {
      setError(err.message || 'Could not save scaffold');
      setSaveState('error');
    } finally {
      setSaving(false);
    }
  }, [loadScaffolds]);

  useEffect(() => {
    if (saveStatusTimerRef.current) {
      clearTimeout(saveStatusTimerRef.current);
      saveStatusTimerRef.current = null;
    }

    if (saveState !== 'saved') return undefined;

    saveStatusTimerRef.current = setTimeout(() => {
      setSaveState((current) => (current === 'saved' ? 'idle' : current));
    }, 1500);

    return () => {
      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = null;
      }
    };
  }, [saveState]);

  useEffect(() => () => {
    if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current);
    if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
  }, []);

  const persistScaffoldOnBlur = useCallback(() => {
    if (!scaffold?.id || loading || saving) return;
    const hasDirtyFields = dirtyFieldNamesRef.current.size > 0;
    const hasDirtyQuestions = dirtyQuestionIndexesRef.current.size > 0;
    if (!hasDirtyFields && !hasDirtyQuestions) return;
    saveScaffold(scaffold);
  }, [loading, saveScaffold, saving, scaffold]);

  useEffect(() => {
    if (!simulateOpen) return undefined;
    if (simulatePhase !== 'chat' && simulatePhase !== 'done') return undefined;

    const rafId = window.requestAnimationFrame(() => {
      if (!simulateChatRef.current) return;
      simulateChatRef.current.scrollTop = simulateChatRef.current.scrollHeight;
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [simulateOpen, simulatePhase, simulateMessages]);

  const handleFieldChange = (field, value) => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    dirtyFieldNamesRef.current.add(field);
    setSavedFieldNames((current) => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
    setScaffold((current) => current ? { ...current, [field]: value } : current);
    setSaveState('dirty');
  };

  const handleMetadataChange = (field, value) => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    const dirtyField = `metadata.${field}`;
    dirtyFieldNamesRef.current.add(dirtyField);
    setSavedFieldNames((current) => {
      if (!current.has(dirtyField)) return current;
      const next = new Set(current);
      next.delete(dirtyField);
      return next;
    });
    setScaffold((current) => {
      if (!current) return current;
      return {
        ...current,
        metadata: {
          ...(current.metadata || {}),
          [field]: value,
        },
      };
    });
    setSaveState('dirty');
  };

  const handleQuestionChange = (index, value) => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    dirtyQuestionIndexesRef.current.add(index);
    setSavedQuestionIndexes((current) => {
      if (!current.has(index)) return current;
      const next = new Set(current);
      next.delete(index);
      return next;
    });
    setScaffold((current) => {
      if (!current) return current;
      const questions = current.questions.map((question, questionIndex) => (
        questionIndex === index ? { ...question, text: value } : question
      ));
      return { ...current, questions };
    });
    setSaveState('dirty');
  };

  const handleAddQuestion = () => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    dirtyFieldNamesRef.current.add('questions');
    setSavedFieldNames((current) => {
      if (!current.has('questions')) return current;
      const next = new Set(current);
      next.delete('questions');
      return next;
    });
    setSavedQuestionIndexes(new Set());
    setScaffold((current) => {
      if (!current) return current;
      const nextQuestions = [
        ...current.questions,
        {
          order: current.questions.length + 1,
          text: '',
          purpose: '',
          answerHint: '',
          metadata: {},
        },
      ];
      dirtyQuestionIndexesRef.current = new Set(nextQuestions.map((_, index) => index));
      return { ...current, questions: nextQuestions };
    });
    setSaveState('dirty');
  };

  const handleDeleteQuestion = (index) => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    dirtyFieldNamesRef.current.add('questions');
    setSavedFieldNames((current) => {
      if (!current.has('questions')) return current;
      const next = new Set(current);
      next.delete('questions');
      return next;
    });
    setSavedQuestionIndexes(new Set());
    setScaffold((current) => {
      if (!current) return current;
      const nextQuestions = current.questions
        .filter((_, questionIndex) => questionIndex !== index)
        .map((question, questionIndex) => ({ ...question, order: questionIndex + 1 }));
      dirtyQuestionIndexesRef.current = new Set(nextQuestions.map((_, questionIndex) => questionIndex));
      return { ...current, questions: nextQuestions };
    });
    setSaveState('dirty');
  };

  const handleReorderQuestions = (fromIndex, toIndex) => {
    if (isScaffoldEditLocked(scaffold?.status)) return;
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return;
    if (fromIndex === toIndex) return;

    dirtyFieldNamesRef.current.add('questions');
    setSavedFieldNames((current) => {
      if (!current.has('questions')) return current;
      const next = new Set(current);
      next.delete('questions');
      return next;
    });
    setSavedQuestionIndexes(new Set());

    setScaffold((current) => {
      if (!current) return current;
      const nextQuestions = [...current.questions];
      if (fromIndex < 0 || fromIndex >= nextQuestions.length) return current;
      if (toIndex < 0 || toIndex >= nextQuestions.length) return current;

      const [moved] = nextQuestions.splice(fromIndex, 1);
      nextQuestions.splice(toIndex, 0, moved);

      const resequenced = nextQuestions.map((question, questionIndex) => ({
        ...question,
        order: questionIndex + 1,
      }));
      dirtyQuestionIndexesRef.current = new Set(resequenced.map((_, questionIndex) => questionIndex));
      return { ...current, questions: resequenced };
    });
    setSaveState('dirty');
  };

  const handleSubmitForReview = async () => {
    if (!scaffold?.id) return;
    try {
      setPublishing(true);
      setError(null);
      const res = await fetch(
        urlMap.submitStoryScaffoldReview(scaffold.id),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
        },
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (payload?.error === 'SCAFFOLD_PUBLISH_VALIDATION_FAILED' && Array.isArray(payload?.details)) {
          const firstKnownDetail = payload.details.find((detail) => SCAFFOLD_PUBLISH_VALIDATION_MESSAGES[detail]);
          if (firstKnownDetail) {
            throw new Error(SCAFFOLD_PUBLISH_VALIDATION_MESSAGES[firstKnownDetail]);
          }
          if (payload.details.length > 0) {
            throw new Error(`Cannot publish scaffold: ${payload.details.join(', ')}`);
          }
        }
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      setScaffold((current) => current
        ? { ...current, status: payload.status || 'in_review' }
        : current);
      await loadScaffolds();
    } catch (err) {
      setError(err.message || 'Could not submit scaffold for review');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!scaffold?.id) return;
    try {
      setPublishing(true);
      setError(null);
      const res = await fetch(urlMap.unpublishStoryScaffold(scaffold.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);

      setScaffold((current) => current
        ? { ...current, status: payload.status || 'draft' }
        : current);
      await loadScaffolds();
    } catch (err) {
      setError(err.message || 'Could not unpublish scaffold');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteScaffold = async () => {
    if (!scaffold?.id) return;
    const scaffoldName = scaffold.title || 'Untitled scaffold';
    const confirmed = window.confirm(`Delete scaffold "${scaffoldName}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setError(null);
      const res = await fetch(urlMap.deleteStoryScaffold(scaffold.id), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }

      await loadScaffolds();
      navigate(appNavigation.shortStoryChat);
    } catch (err) {
      setError(err.message || 'Could not delete scaffold');
    }
  };

  const handleOpenSimulate = () => {
    if (!scaffold?.id) return;
    const hasQuestions = Array.isArray(scaffold.questions) && scaffold.questions.length > 0;
    const allQuestionsFilled = hasQuestions
      && scaffold.questions.every((question) => String(question?.text ?? '').trim().length > 0);
    if (!allQuestionsFilled) {
      setError('Simulation is available only for finished scaffolds with all questions filled.');
      return;
    }

    setSimulateOpen(true);
    setSimulatePhase('loading');
    setSimulateMessages([]);
    setSimulateCurrentAnswer('');
    setSimulateSessionId(null);
    setSimulateAiSessionId(null);
    setSimulateQuestionIndex(0);
    setSimulateWarnedBlank(false);
    setSimulateError(null);
    setSimulateHistoryActiveId(null);

    const loadSimulationHistory = async () => {
      if (!scaffold?.id) return;
      setSimulateHistoryLoading(true);
      setSimulateHistoryError(null);
      try {
        const res = await fetch(urlMap.listScaffoldSimulations(scaffold.id, 20), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
        });
        const payload = await res.json().catch(() => []);
        if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);
        setSimulateHistory(Array.isArray(payload) ? payload : []);
      } catch (historyErr) {
        setSimulateHistoryError(historyErr?.message || 'Could not load simulation history.');
      } finally {
        setSimulateHistoryLoading(false);
      }
    };

    loadSimulationHistory();

    postJson(urlMap.shortStoryStart(), { storyScaffoldId: scaffold.id, fresh: true })
      .then((result) => {
        const initialMessages = result.previousMessages ?? [{ type: 'question', text: result.question }];

        if (result.done) {
          setSimulateMessages([
            ...initialMessages,
            { type: 'question', text: 'Great - I have everything I need. Click "Generate my draft" to bring your story to life.' },
          ]);
          setSimulatePhase('done');
          setSimulateQuestionIndex(result.total);
        } else {
          setSimulateMessages(initialMessages);
          setSimulatePhase('chat');
          setSimulateQuestionIndex(result.questionIndex);
        }

        setSimulateSessionId(result.sessionId);
        setSimulateTotal(result.total || scaffold.questions.length || 1);
      })
      .catch((err) => {
        if (err?.body?.error === 'INSUFFICIENT_FIG_BALANCE') {
          const required = Number(err?.body?.requiredFigCoins ?? 0);
          const balance = Number(err?.body?.balance ?? 0);
          setSimulateError(`Not enough Fig coins to start this scaffold (${balance}/${required}).`);
        } else {
          setSimulateError('Could not start the questionnaire. Please try again.');
        }
        setSimulatePhase('error');
      });

    postJson(urlMap.startScaffoldSimulation(), { storyScaffoldId: scaffold.id, language: scaffold.questionLanguage || 'en' })
      .then((result) => {
        if (result?.simulationId) {
          setSimulateAiSessionId(result.simulationId);
          setSimulateHistoryActiveId(result.simulationId);
          loadSimulationHistory();
        }
      })
      .catch(() => {
        setSimulateAiSessionId(null);
      });
  };

  const handleDeleteSimulationHistoryItem = async (simulationId) => {
    if (!simulationId) return;
    try {
      const res = await fetch(urlMap.deleteScaffoldSimulation(simulationId), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!res.ok && res.status !== 404) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }

      setSimulateHistory((current) => current.filter((item) => item.id !== simulationId));
      if (simulateHistoryActiveId === simulationId) {
        setSimulateHistoryActiveId(null);
      }
    } catch (historyErr) {
      setSimulateHistoryError(historyErr?.message || 'Could not delete simulation history item.');
    }
  };

  const handleOpenSimulationHistoryItem = async (simulationId) => {
    if (!simulationId) return;
    try {
      setSimulateError(null);
      setSimulateHistoryError(null);
      setSimulateHistoryActiveId(simulationId);

      const res = await fetch(urlMap.getScaffoldSimulation(simulationId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);

      const mappedMessages = Array.isArray(payload.messages)
        ? payload.messages.map((msg) => ({ type: msg.type, text: msg.text }))
        : [];

      if ((!mappedMessages.length || !mappedMessages.some((msg) => msg.type === 'story')) && payload.generatedStory) {
        mappedMessages.push({ type: 'story', text: payload.generatedStory });
      }

      if (Array.isArray(payload.generatedTasks) && payload.generatedTasks.length > 0) {
        payload.generatedTasks.forEach((task, index) => {
          const title = String(task?.title || `Task ${index + 1}`).trim();
          const description = String(task?.description || '').trim();
          const text = description ? `${index + 1}. ${title}\n${description}` : `${index + 1}. ${title}`;
          mappedMessages.push({ type: 'task', text });
        });
      }

      setSimulateMessages(mappedMessages);
      setSimulateAiSessionId(simulationId);
      setSimulateTotal(Number(payload.totalQuestions || 1));
      setSimulateQuestionIndex(Number(payload.questionIndex || 0));

      const status = String(payload.status || 'chat');
      if (status === 'generated' || status === 'ready') {
        setSimulatePhase('done');
      } else if (status === 'failed') {
        setSimulatePhase('error');
        setSimulateError(payload.errorMessage || 'Simulation failed.');
      } else {
        setSimulatePhase('chat');
      }
    } catch (historyErr) {
      setSimulateHistoryError(historyErr?.message || 'Could not open simulation history item.');
    }
  };

  const syncAiSimulationAnswer = async (answer) => {
    if (!simulateAiSessionId) return;
    try {
      await postJson(urlMap.answerScaffoldSimulation(simulateAiSessionId), { answer });
    } catch {
      // Keep chat simulation usable even if AI suggestion-side session falls out of sync.
    }
  };

  const submitSimulateAnswer = async (answer) => {
    const result = await postJson(urlMap.shortStoryAnswer(), {
      sessionId: simulateSessionId,
      answer,
      questionIndex: simulateQuestionIndex,
      storyScaffoldId: scaffold?.id,
    });

    setSimulateMessages((prev) => {
      const next = [...prev, { type: 'answer', text: answer }];
      if (result.done) {
        next.push({ type: 'question', text: 'Great - I have everything I need. Click "Generate my draft" to bring your story to life.' });
      } else {
        next.push({ type: 'question', text: result.nextQuestion });
      }
      return next;
    });

    setSimulateCurrentAnswer('');
    if (result.done) {
      setSimulatePhase('done');
    } else {
      setSimulateQuestionIndex(result.nextQuestionIndex);
    }

    await syncAiSimulationAnswer(answer);
  };

  const handleSimulateGenerateAnswer = async () => {
    if (!simulateAiSessionId) {
      setSimulateError('AI answer generation is not available for this simulation. Please restart simulation and try again.');
      return;
    }

    setSimulateSuggesting(true);
    setSimulateError(null);
    try {
      const result = await postJson(urlMap.suggestScaffoldSimulationAnswer(simulateAiSessionId), {});
      const suggestion = String(result?.suggestion ?? '').trim();
      if (!suggestion) {
        setSimulateError('Could not generate an AI answer. Please try again.');
        return;
      }
      setSimulateCurrentAnswer(suggestion);
      setSimulateWarnedBlank(false);
    } catch (err) {
      if (err?.body?.error === 'INSUFFICIENT_FIG_BALANCE') {
        const required = Number(err?.body?.requiredFigCoins ?? AI_SUGGEST_FIG_COST);
        const balance = Number(err?.body?.balance ?? 0);
        setSimulateError(`Not enough Fig coins to generate an answer (${balance}/${required}).`);
      } else {
        setSimulateError(err?.message || 'Could not generate an AI answer. Please try again.');
      }
    } finally {
      setSimulateSuggesting(false);
    }
  };

  const handleSimulateNext = async () => {
    const trimmed = simulateCurrentAnswer.trim();

    if (!trimmed && simulateUseAiAnswers) {
      await handleSimulateGenerateAnswer();
      return;
    }

    if (!trimmed && !simulateWarnedBlank) {
      setSimulateWarnedBlank(true);
      setSimulateMessages((prev) => [
        ...prev,
        { type: 'question', text: 'No worries - even a quick "I don\'t know" helps. What comes to mind?' },
      ]);
      return;
    }

    const answer = trimmed || "I don't know";
    setSimulateSubmitting(true);
    setSimulateWarnedBlank(false);

    try {
      await submitSimulateAnswer(answer);
    } catch (err) {
      setSimulateError(err?.message || 'Could not submit answer. Please try again.');
    } finally {
      setSimulateSubmitting(false);
    }
  };

  const handleSimulateGenerate = () => {
    if (!simulateAiSessionId) {
      setSimulateError('Draft generation is not available for this simulation. Please restart simulation and try again.');
      return;
    }

    setSimulateGeneratingDraft(true);
    setSimulateError(null);

    postJson(urlMap.generateScaffoldSimulation(simulateAiSessionId), {})
      .then((result) => {
        const mappedMessages = Array.isArray(result?.messages)
          ? result.messages.map((msg) => ({ type: msg.type, text: msg.text }))
          : [];

        if ((!mappedMessages.length || !mappedMessages.some((msg) => msg.type === 'story')) && result?.generatedStory) {
          mappedMessages.push({ type: 'story', text: result.generatedStory });
        }

        if (Array.isArray(result?.generatedTasks) && result.generatedTasks.length > 0) {
          result.generatedTasks.forEach((task, index) => {
            const title = String(task?.title || `Task ${index + 1}`).trim();
            const description = String(task?.description || '').trim();
            const text = description ? `${index + 1}. ${title}\n${description}` : `${index + 1}. ${title}`;
            mappedMessages.push({ type: 'task', text });
          });
        }

        setSimulateMessages(mappedMessages);
        setSimulatePhase('done');
      })
      .catch((err) => {
        if (err?.body?.error === 'INSUFFICIENT_FIG_BALANCE') {
          const required = Number(err?.body?.requiredFigCoins ?? DRAFT_GENERATE_FIG_COST);
          const balance = Number(err?.body?.balance ?? 0);
          setSimulateError(`Not enough Fig coins to generate draft (${balance}/${required}).`);
        } else {
          setSimulateError(err?.message || 'Could not generate draft. Please try again.');
        }
      })
      .finally(() => {
        setSimulateGeneratingDraft(false);
      });
  };

  const handleSimulateKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!simulateSubmitting) handleSimulateNext();
    }
  };

  const isPublished = scaffold?.status === 'published' || scaffold?.status === 'active';
  const isInReview = scaffold?.status === 'in_review';
  const isEditLocked = isScaffoldEditLocked(scaffold?.status);
  const reviewFeedback = scaffold?.metadata && typeof scaffold.metadata === 'object'
    ? scaffold.metadata.reviewFeedback
    : null;
  const hasQuestions = Array.isArray(scaffold?.questions) && scaffold.questions.length > 0;
  const allQuestionsFilled = hasQuestions
    && scaffold.questions.every((question) => String(question?.text ?? '').trim().length > 0);
  const canSimulate = Boolean(scaffold?.id) && allQuestionsFilled;
  const systemPromptExcerpt = buildExcerpt(scaffold?.systemPrompt);
  const taskSystemPromptExcerpt = buildExcerpt(scaffold?.taskSystemPrompt);
  const saveLabel = saveState === 'saving' || saving
    ? 'Saving...'
    : saveState === 'dirty'
      ? 'Unsaved changes'
      : saveState === 'error'
        ? 'Save failed'
        : saveState === 'saved'
          ? 'Saved'
          : '';

  return (
    <div className="home-page scaffold-editor-page">
      <TopBar />
      <main className="home-main scaffold-editor-layout">
        <aside className="scaffold-editor-menu" aria-label="Scaffolds">
          <div className="scaffold-editor-menu__header">
            <Link to={appNavigation.home} className="short-story-back">← Home</Link>
            <h2 className="scaffold-editor-menu__title">Scaffolds</h2>
          </div>

          {scaffoldsLoading && <div className="projects-empty">Loading scaffolds...</div>}
          {scaffoldsError && <div className="projects-error">{scaffoldsError}</div>}

          {!scaffoldsLoading && !scaffoldsError && (
            <nav className="scaffold-editor-menu__list">
              {scaffolds.map((item) => {
                const itemPublished = item.status === 'published' || item.status === 'active';
                const itemStatusLabel = scaffoldStatusLabel(item.status);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`scaffold-editor-menu__item ${item.id === scaffoldId ? 'active' : ''}`}
                    onClick={() => navigate(appNavigation.scaffoldEditor(item.id))}
                  >
                    <span>{item.title || 'Untitled scaffold'}</span>
                    <span className="scaffold-editor-menu__meta">
                      <span className={`scaffold-editor-menu__status ${itemPublished ? 'published' : item.status === 'in_review' ? 'review' : 'draft'}`}>
                        {itemStatusLabel}
                      </span>
                      <span className="scaffold-editor-menu__language">{languageLabel(item.questionLanguage || item.language || 'en')}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
          )}
        </aside>

        <section className="drafts-section scaffold-editor scaffold-editor--page" aria-label="Scaffold editor">
          <div className="scaffold-editor__topbar">
            <div>
              <h2 className="scaffold-editor__page-title">Edit Scaffold</h2>
              {scaffold ? (
                <span className={`scaffold-editor__status ${isPublished ? 'published' : isInReview ? 'review' : 'draft'}`}>
                  {scaffoldStatusLabel(scaffold.status)}
                </span>
              ) : null}
            </div>
            <div className="scaffold-editor__top-actions">
              <span className={`scaffold-editor__save-state ${saveState}`}>{saveLabel}</span>
              {scaffold ? (
                <>
                  <button
                    type="button"
                    className="scaffold-editor__simulate"
                    onClick={handleOpenSimulate}
                    disabled={publishing || !canSimulate}
                    title={!canSimulate ? 'Requires non-empty questions to simulate' : 'Start scaffold questionnaire simulation'}
                  >
                    Simulate
                  </button>
                  <button
                    type="button"
                    className="scaffold-editor__publish"
                    onClick={isPublished ? handleUnpublish : handleSubmitForReview}
                    disabled={publishing || isInReview}
                  >
                    {publishing ? 'Updating...' : isPublished ? 'Unpublish' : isInReview ? 'In Review' : 'Submit for review'}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {!!reviewFeedback && (
            <div className="projects-error" role="status">
              Admin feedback: {String(reviewFeedback)}
            </div>
          )}

          {isEditLocked && (
            <div className="projects-empty" role="status">
              Editing is locked while this scaffold is in review or published.
            </div>
          )}

          {loading && <div className="projects-empty">Loading scaffold questions...</div>}
          {error && <div className="projects-error">{error}</div>}

          {!loading && scaffold && (
            <>
              <label className="scaffold-editor__field">
                <span>Publisher Name</span>
                <input
                  value={scaffold.publisherName}
                  onChange={(e) => handleFieldChange('publisherName', e.target.value)}
                  onBlur={persistScaffoldOnBlur}
                  disabled={isEditLocked}
                />
              </label>

              <label className="scaffold-editor__field">
                <span>
                  Title <span className="scaffold-editor__var">{'{{scaffoldTitle}}'}</span>
                </span>
                <input
                  className={savedFieldNames.has('title') ? 'scaffold-editor__field-input saved' : 'scaffold-editor__field-input'}
                  value={scaffold.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  onBlur={persistScaffoldOnBlur}
                  disabled={isEditLocked}
                />
              </label>

              <label className="scaffold-editor__field">
                <span>
                  Description <span className="scaffold-editor__var">{'{{scaffoldDescription}}'}</span>
                </span>
                <textarea
                  value={scaffold.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  onBlur={persistScaffoldOnBlur}
                  rows={3}
                  disabled={isEditLocked}
                />
              </label>

              <label className="scaffold-editor__field">
                <span>
                  Language <span className="scaffold-editor__var">{'{{languageName}}'}</span>
                </span>
                <select
                  value={scaffold.questionLanguage || 'en'}
                  onChange={(e) => handleFieldChange('questionLanguage', e.target.value)}
                  onBlur={persistScaffoldOnBlur}
                  disabled={isPublished || isEditLocked}
                  title={isPublished ? 'Language is locked after publish. Unpublish to change it.' : isEditLocked ? 'Language is locked while scaffold is in review.' : 'Select scaffold language'}
                >
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>{language.label}</option>
                  ))}
                </select>
              </label>

              <section className="scaffold-editor__prompt" aria-label={t('scaffoldEditor.systemScaffoldAriaLabel')}>
                <div className="scaffold-editor__translations-header">
                  <div
                    className="scaffold-editor__tree-trigger"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSystemPromptExpanded((current) => !current)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSystemPromptExpanded((current) => !current);
                      }
                    }}
                    aria-label={t('scaffoldEditor.toggleSystemScaffold')}
                    aria-expanded={systemPromptExpanded}
                  >
                    <span className="scaffold-editor__tree-glyph">{systemPromptExpanded ? '▾' : '▸'}</span>
                    <h3>
                      {t('scaffoldEditor.systemScaffoldTitle')}
                      <button
                        type="button"
                        className="scaffold-editor__info-icon"
                        title={t('scaffoldEditor.systemScaffoldInfo')}
                        aria-label={t('scaffoldEditor.systemScaffoldInfoLabel')}
                        aria-expanded={systemScaffoldInfoOpen}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSystemScaffoldInfoOpen((current) => !current);
                        }}
                      >
                        i
                      </button>
                    </h3>
                    <span className="scaffold-editor__prompt-excerpt" title={systemPromptExcerpt}>{systemPromptExcerpt}</span>
                  </div>
                </div>
                {systemScaffoldInfoOpen && (
                  <p className="scaffold-editor__task-complexity-inline-help">
                    {t('scaffoldEditor.systemScaffoldInfo')}
                  </p>
                )}
                {systemPromptExpanded && (
                  <>
                    <label className="scaffold-editor__field">
                      <span>{t('scaffoldEditor.promptTemplateLabel')}</span>
                      <textarea
                        className={savedFieldNames.has('systemPrompt') ? 'scaffold-editor__question-input saved' : 'scaffold-editor__question-input'}
                        value={scaffold.systemPrompt}
                        onChange={(e) => handleFieldChange('systemPrompt', e.target.value)}
                        onBlur={persistScaffoldOnBlur}
                        rows={12}
                        disabled={isEditLocked}
                      />
                    </label>
                    <div className="scaffold-editor__prompt-help">
                      <div>
                        {t('scaffoldEditor.availablePlaceholders')}: <code>{'{{languageName}}'}</code>, <code>{'{{scaffoldTitle}}'}</code>, <code>{'{{scaffoldDescription}}'}</code>, <code>{'{{variableLines}}'}</code>, <code>{'{{questionLines}}'}</code>, <code>{'{{answersJson}}'}</code>, <code>{'{{q1}}'}</code>, <code>{'{{r1}}'}</code>, <code>{'{{q2}}'}</code>, <code>{'{{r2}}'}</code>...
                        <br />
                        {t('scaffoldEditor.runtimeAnswersExplainer')}
                      </div>
                      <div>
                        {t('scaffoldEditor.questionAnswerPlaceholderExplainer')}
                      </div>
                    </div>
                  </>
                )}
              </section>

              <section className="scaffold-editor__task-complexity-help" aria-label={t('scaffoldEditor.taskComplexityAriaLabel')}>
                <div className="scaffold-editor__translations-header">
                  <div
                    className="scaffold-editor__tree-trigger"
                    role="button"
                    tabIndex={0}
                    onClick={() => setTaskSystemPromptExpanded((current) => !current)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setTaskSystemPromptExpanded((current) => !current);
                      }
                    }}
                    aria-label="Toggle task system prompt"
                    aria-expanded={taskSystemPromptExpanded}
                  >
                    <span className="scaffold-editor__tree-glyph">{taskSystemPromptExpanded ? '▾' : '▸'}</span>
                    <h3>Task System Prompt</h3>
                    <span className="scaffold-editor__prompt-excerpt" title={taskSystemPromptExcerpt}>{taskSystemPromptExcerpt}</span>
                  </div>
                </div>
                {taskSystemPromptExpanded && (
                  <label className="scaffold-editor__field">
                    <span>Task System Prompt</span>
                    <textarea
                      className={savedFieldNames.has('taskSystemPrompt') ? 'scaffold-editor__question-input saved' : 'scaffold-editor__question-input'}
                      value={scaffold.taskSystemPrompt}
                      onChange={(e) => handleFieldChange('taskSystemPrompt', e.target.value)}
                      onBlur={persistScaffoldOnBlur}
                      rows={10}
                      disabled={isEditLocked}
                    />
                  </label>
                )}
                <label className="scaffold-editor__field scaffold-editor__task-complexity-row">
                  <span className="scaffold-editor__task-complexity-subtitle">
                    {t('scaffoldEditor.taskComplexityTitle')}
                    <button
                      type="button"
                      className="scaffold-editor__info-icon"
                      title={t('scaffoldEditor.taskComplexityInfo')}
                      aria-label={t('scaffoldEditor.taskComplexityInfoLabel')}
                      aria-expanded={taskComplexityInfoOpen}
                      onClick={() => setTaskComplexityInfoOpen((current) => !current)}
                    >
                      i
                    </button>
                  </span>
                  <select
                    value={String(scaffold.metadata?.taskComplexity || 'easy').toLowerCase()}
                    onChange={(e) => handleMetadataChange('taskComplexity', e.target.value)}
                    onBlur={persistScaffoldOnBlur}
                    disabled={isEditLocked}
                    title={t('scaffoldEditor.taskComplexitySelectTitle')}
                  >
                    {TASK_COMPLEXITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </select>
                </label>
                {taskComplexityInfoOpen && (
                  <p className="scaffold-editor__task-complexity-inline-help">
                    {t('scaffoldEditor.taskComplexityInfo')}
                  </p>
                )}
              </section>

              <fieldset className="scaffold-editor__questions" aria-label="Scaffold questions">
                <div className="scaffold-editor__questions-header">
                  <legend className="scaffold-editor__questions-legend">
                    Questions <span className="scaffold-editor__var">{'{{variableLines}}'}</span>{' '}
                    <span className="scaffold-editor__var">{'{{questionLines}}'}</span>
                  </legend>
                  <div className="scaffold-editor__questions-actions">
                    <span className="scaffold-editor__question-count">
                      {scaffold.questions.length} question{scaffold.questions.length === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      className="scaffold-editor__question-add"
                      onClick={handleAddQuestion}
                      disabled={isEditLocked}
                    >
                      Add Question
                    </button>
                  </div>
                </div>

                {scaffold.questions.length === 0 ? (
                  <div className="projects-empty">No questions yet. Add at least one question to publish and simulate.</div>
                ) : (
                  <div className="scaffold-editor__question-list">
                    {scaffold.questions.map((question, index) => {
                      const variableName = `q${question.order || index + 1}`;
                      return (
                      <div
                        className={`scaffold-editor__question-row ${dragOverQuestionIndex === index ? 'drag-over' : ''}`}
                        key={question.id || `${scaffold.id}-${question.order || index}`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          if (dragOverQuestionIndex !== index) setDragOverQuestionIndex(index);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (dragQuestionIndex !== null) handleReorderQuestions(dragQuestionIndex, index);
                          setDragQuestionIndex(null);
                          setDragOverQuestionIndex(null);
                        }}
                        onDragEnd={() => {
                          setDragQuestionIndex(null);
                          setDragOverQuestionIndex(null);
                        }}
                      >
                        <button
                          type="button"
                          className="scaffold-editor__question-drag"
                          draggable={!isEditLocked}
                          onDragStart={(event) => {
                            if (isEditLocked) return;
                            setDragQuestionIndex(index);
                            setDragOverQuestionIndex(index);
                            event.dataTransfer.effectAllowed = 'move';
                          }}
                          aria-label={`Drag to reorder question ${index + 1}`}
                          title="Drag to reorder"
                          disabled={isEditLocked}
                        >
                          ≡
                        </button>
                        <label className="scaffold-editor__field scaffold-editor__question-field">
                          <span>
                            Question {question.order || index + 1}
                            {' '}
                            <span className="scaffold-editor__var">{variableName}</span>
                          </span>
                          <textarea
                            className={savedQuestionIndexes.has(index) ? 'scaffold-editor__question-input saved' : 'scaffold-editor__question-input'}
                            value={question.text}
                            onChange={(e) => handleQuestionChange(index, e.target.value)}
                            onBlur={persistScaffoldOnBlur}
                            placeholder={`Prompt for ${variableName}`}
                            rows={2}
                            disabled={isEditLocked}
                          />
                        </label>
                        <button
                          type="button"
                          className="scaffold-editor__question-delete"
                          onClick={() => handleDeleteQuestion(index)}
                          disabled={isEditLocked}
                        >
                          Delete
                        </button>
                      </div>
                      );
                    })}
                  </div>
                )}
              </fieldset>

              <section className="scaffold-editor__danger-zone" aria-label="Danger zone">
                <h3>Danger Zone</h3>
                <button
                  type="button"
                  className="scaffold-editor__delete"
                  onClick={handleDeleteScaffold}
                  disabled={publishing || saving}
                >
                  Delete Scaffold
                </button>
              </section>
            </>
          )}

          {simulateOpen && (
            <div className="scaffold-sim-overlay" role="dialog" aria-modal="true" aria-label="Simulate Scaffold Questionaire" onClick={() => setSimulateOpen(false)}>
              <aside className="scaffold-sim-panel" onClick={(event) => event.stopPropagation()}>
                <div className="scaffold-sim-panel__header">
                  <h3>Simulate Scaffold Questionaire</h3>
                  <div className="scaffold-sim-panel__header-actions">
                    <CoinBalance />
                    <button type="button" className="scaffold-sim-panel__close" aria-label="Close simulation panel" onClick={() => setSimulateOpen(false)}>
                      x
                    </button>
                  </div>
                </div>

                <div className="scaffold-sim-panel__body">
                  <div className="scaffold-sim-panel__history">
                    <div
                      className="scaffold-sim-panel__history-title"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSimulateHistoryExpanded((current) => !current)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSimulateHistoryExpanded((current) => !current);
                        }
                      }}
                    >
                      <span className="scaffold-sim-panel__history-glyph">{simulateHistoryExpanded ? '▾' : '▸'}</span>
                      Recent simulations
                    </div>

                    {simulateHistoryExpanded && (
                      <div className="scaffold-sim-panel__history-list">
                        {simulateHistoryLoading && <div className="scaffold-sim-panel__state">Loading history...</div>}
                        {simulateHistoryError && !simulateHistoryLoading && (
                          <div className="scaffold-sim-panel__state scaffold-sim-panel__state--error">{simulateHistoryError}</div>
                        )}
                        {!simulateHistoryLoading && !simulateHistoryError && simulateHistory.length === 0 && (
                          <div className="scaffold-sim-panel__state">No simulations yet.</div>
                        )}

                        {!simulateHistoryLoading && !simulateHistoryError && simulateHistory.map((item) => (
                          <div
                            key={item.id}
                            className={`scaffold-sim-panel__history-item ${simulateHistoryActiveId === item.id ? 'active' : ''}`}
                          >
                            <button
                              type="button"
                              className="scaffold-sim-panel__history-open"
                              onClick={() => handleOpenSimulationHistoryItem(item.id)}
                              title="Open simulation"
                            >
                              <span className="scaffold-sim-panel__history-status">{item.status}</span>
                              <span className="scaffold-sim-panel__history-preview">
                                {item.lastAnswerExcerpt || item.storyPreview || `${Math.min(Number(item.questionIndex ?? 0) + 1, Number(item.totalQuestions ?? 1))}/${Number(item.totalQuestions ?? 1)} questions`}
                              </span>
                            </button>

                            <button
                              type="button"
                              className="scaffold-sim-panel__history-delete"
                              onClick={() => handleDeleteSimulationHistoryItem(item.id)}
                              title="Delete simulation"
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {simulatePhase === 'loading' && (
                    <div className="scaffold-sim-panel__state">Starting your questionnaire...</div>
                  )}

                  {simulatePhase === 'error' && (
                    <div className="scaffold-sim-panel__state scaffold-sim-panel__state--error">{simulateError || 'Could not start the questionnaire. Please try again.'}</div>
                  )}

                  {(simulatePhase === 'chat' || simulatePhase === 'done') && (
                    <>
                      <div className="scaffold-sim-panel__progress">
                        {simulatePhase === 'done'
                          ? `${simulateTotal} of ${simulateTotal}`
                          : `Question ${Math.min(simulateQuestionIndex + 1, simulateTotal)} of ${simulateTotal}`}
                      </div>

                      <div className="scaffold-sim-panel__chat" ref={simulateChatRef}>
                        {simulateMessages.map((msg, idx) => {
                          const isTaskStart = msg.type === 'task' && simulateMessages[idx - 1]?.type !== 'task';
                          return (
                            <React.Fragment key={`${msg.type}-${idx}`}>
                              {isTaskStart && (
                                <div className="scaffold-sim-panel__task-divider" role="separator" aria-label="Tasks section">
                                  <span>Tasks</span>
                                </div>
                              )}
                              <div className={`scaffold-sim-panel__bubble-row ${msg.type === 'answer' ? 'right' : 'left'}`}>
                                <div className={`scaffold-sim-panel__bubble ${msg.type}`}>{msg.text}</div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      <div className="scaffold-sim-panel__input">
                        {simulatePhase === 'done' ? (
                          <div className="scaffold-sim-panel__input-actions">
                            <button
                              type="button"
                              className="scaffold-editor__simulate"
                              onClick={handleSimulateGenerate}
                              disabled={simulateGeneratingDraft}
                            >
                              {simulateGeneratingDraft ? 'Generating draft...' : `Generate draft (${DRAFT_GENERATE_FIG_COST} figs)`}
                            </button>
                            <button
                              type="button"
                              className="scaffold-editor__publish"
                              onClick={handleOpenSimulate}
                              disabled={simulateGeneratingDraft}
                            >
                              Start new simulation
                            </button>
                          </div>
                        ) : (
                          <>
                            <textarea
                              className="scaffold-editor__question-input"
                              value={simulateCurrentAnswer}
                              rows={5}
                              placeholder="Your answer..."
                              onChange={(event) => {
                                setSimulateCurrentAnswer(event.target.value);
                                setSimulateWarnedBlank(false);
                              }}
                              onKeyDown={handleSimulateKeyDown}
                              disabled={simulateSubmitting}
                            />
                            <div className="scaffold-sim-panel__input-actions">
                              <label className="scaffold-sim-panel__autosuggest">
                                <input
                                  type="checkbox"
                                  checked={simulateUseAiAnswers}
                                  onChange={(event) => setSimulateUseAiAnswers(event.target.checked)}
                                  disabled={simulateSubmitting || simulateSuggesting}
                                />
                                Use AI for answers
                              </label>
                              {simulateUseAiAnswers && (
                                <button
                                  type="button"
                                  className="scaffold-editor__simulate"
                                  onClick={handleSimulateGenerateAnswer}
                                  disabled={simulateSubmitting || simulateSuggesting}
                                >
                                  {simulateSuggesting ? 'Generating...' : `Generate answer (${AI_SUGGEST_FIG_COST} fig)`}
                                </button>
                              )}
                              <button
                                type="button"
                                className="scaffold-editor__publish"
                                onClick={handleSimulateNext}
                                disabled={simulateSubmitting || simulateSuggesting}
                              >
                                {simulateSubmitting ? '...' : simulateQuestionIndex === simulateTotal - 1 ? 'Done' : 'Next'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </aside>
            </div>
          )}

        </section>
      </main>
    </div>
  );
}